import React, { useEffect, useMemo, useState } from 'react';
import { Search, MapPin, Phone, Mail, Loader2, AlertCircle, School } from 'lucide-react';
import { loadDirectory, filterSchools, PARISHES, countLevels, TOTAL_INSTITUTIONS } from '../lib/schoolDirectory';
import { ShowMoreList } from './shared';

// A searchable listing over the full MOEY directory, scoped to a set of levels
// by the page that mounts it. Built so the "Prep & Primary" door can finally
// list actual schools — until now the whole hub was guidance with not one named
// school in it, because no primary data existed in the repo at all.
//
// The 508KB dataset is fetched on mount rather than imported, so pages that
// never show a listing don't pay for it.
export const SchoolDirectoryPanel = ({
  levels,
  levelOptions,
  initialLevel = '',
  title = 'School Directory',
  blurb,
  initial = 10,
}) => {
  const [schools, setSchools] = useState(null);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [parish, setParish] = useState('');
  const [sector, setSector] = useState('');
  // When the caller offers a level picker, an empty value means "all of them".
  const [level, setLevel] = useState(initialLevel);
  // MOEY's own school type (Infant, All Age, Primary & Junior High,
  // Technical High, Community College...). Broad level is often not what
  // someone is actually looking for.
  const [type, setType] = useState('');

  useEffect(() => {
    let live = true;
    loadDirectory().then((rows) => {
      if (!live) return;
      if (!rows.length) setFailed(true);
      setSchools(rows);
    });
    return () => {
      live = false;
    };
  }, []);

  // No `levels` means the whole directory, not zero of it.
  const expected = useMemo(
    () => (levels && levels.length ? countLevels(levels) : TOTAL_INSTITUTIONS),
    [levels]
  );

  const activeLevels = level ? [level] : levels;

  // Types offered are derived from the rows currently in scope, so the menu
  // never lists a type that would return nothing.
  const typeOptions = useMemo(() => {
    if (!schools) return [];
    const inScope = filterSchools(schools, { levels: activeLevels });
    return [...new Set(inScope.map((s) => s.type).filter(Boolean))].sort();
  }, [schools, activeLevels]);
  const results = useMemo(
    () => (schools ? filterSchools(schools, { levels: activeLevels, type, parish, sector, search }) : []),
    [schools, activeLevels, type, parish, sector, search]
  );

  const selectClass =
    'px-3 min-h-[44px] rounded-xl bg-white ring-1 ring-hairline text-[15px] text-ink ' +
    'focus:ring-2 focus:ring-brand-500';

  return (
    <section className="mb-8">
      <div className="mb-5 flex items-start gap-4">
        <span
          className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100"
          aria-hidden="true"
        >
          <School className="w-5 h-5 text-brand-700" />
        </span>
        <div>
          <h2 className="text-xl md:text-2xl font-extrabold text-ink">{title}</h2>
          <p className="text-[15px] text-ink-muted mt-1">
            {blurb || `${expected.toLocaleString()} institutions across all 14 parishes.`}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
        <div className="relative flex-1">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle"
            aria-hidden="true"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name, address or parish…"
            aria-label="Search schools"
            className="w-full pl-10 pr-3 min-h-[44px] rounded-xl bg-white ring-1 ring-hairline text-[15px] text-ink placeholder:text-ink-subtle focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {levelOptions && levelOptions.length > 0 && (
          <select
            value={level}
            onChange={(e) => { setLevel(e.target.value); setType(''); }}
            aria-label="Filter by education level"
            className={selectClass}
          >
            <option value="">All levels</option>
            {levelOptions.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        )}
        {typeOptions.length > 1 && (
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label="Filter by school type"
            className={selectClass}
          >
            <option value="">All types</option>
            {typeOptions.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        )}
        <select
          value={parish}
          onChange={(e) => setParish(e.target.value)}
          aria-label="Filter by parish"
          className={selectClass}
        >
          <option value="">All parishes</option>
          {PARISHES.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          value={sector}
          onChange={(e) => setSector(e.target.value)}
          aria-label="Filter by sector"
          className={selectClass}
        >
          <option value="">Public &amp; independent</option>
          <option value="Public">Public</option>
          <option value="Independent">Independent</option>
        </select>
      </div>

      {schools === null && (
        <p className="flex items-center gap-2 text-[15px] text-ink-muted py-6">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          Loading the school directory…
        </p>
      )}

      {failed && (
        <p role="alert" className="flex items-start gap-2 text-[15px] text-amber-900 bg-amber-50 ring-1 ring-amber-200 rounded-xl p-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
          The school directory could not be loaded. Please refresh, or try again shortly.
        </p>
      )}

      {schools !== null && !failed && (
        <>
          <p className="text-[15px] text-ink-muted mb-3" aria-live="polite">
            {results.length.toLocaleString()} {results.length === 1 ? 'school' : 'schools'} found
          </p>

          {results.length === 0 ? (
            <p className="text-[15px] text-ink-muted bg-canvas-sunken ring-1 ring-hairline rounded-xl p-6 text-center">
              No schools match those filters. Try a different parish or clear the search.
            </p>
          ) : (
            <ShowMoreList items={results} initial={initial} noun="schools" className="space-y-2">
              {(s) => (
                <article
                  key={s.code}
                  className="bg-canvas-raised ring-1 ring-hairline rounded-xl p-4 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold text-[15px] text-ink">{s.name}</h3>
                    <span className="flex-shrink-0 text-[13px] font-semibold px-2 py-0.5 rounded-full bg-canvas-sunken text-ink-muted ring-1 ring-hairline">
                      {s.sector}
                    </span>
                  </div>
                  <p className="text-sm text-ink-muted mt-1">{s.type}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-ink-muted">
                    {s.address && (
                      <span className="inline-flex items-start gap-1.5">
                        <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        {s.address}
                      </span>
                    )}
                    <span className="font-medium">{s.parish}</span>
                  </div>
                  {(s.phone || s.email) && (
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm">
                      {s.phone && (
                        <a
                          href={`tel:${s.phone.replace(/\s+/g, '')}`}
                          className="inline-flex items-center gap-1.5 min-h-[36px] text-brand-700 hover:underline underline-offset-4"
                        >
                          <Phone className="w-4 h-4" aria-hidden="true" />
                          {s.phone}
                        </a>
                      )}
                      {s.email && (
                        <a
                          href={`mailto:${s.email.split(' / ')[0]}`}
                          className="inline-flex items-center gap-1.5 min-h-[36px] text-brand-700 hover:underline underline-offset-4 break-all"
                        >
                          <Mail className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                          {s.email.split(' / ')[0]}
                        </a>
                      )}
                    </div>
                  )}
                </article>
              )}
            </ShowMoreList>
          )}

          <p className="text-[13px] text-ink-subtle mt-4">
            Source: Ministry of Education, Youth &amp; Information — Directory of Educational
            Institutions 2018/2019. Contact details may have changed since publication.
          </p>
        </>
      )}
    </section>
  );
};

export default SchoolDirectoryPanel;
